/**
 * The triage of the reports sent from the app, for the system administrator
 * (Codex 1022). Every report arrives Pending and goes no further by itself: it is
 * read here with its pictures and its context, what it says can be changed (what
 * was sent stays shown beside it), and To process passes it on. That press is the
 * owner's go, and what it passes on is all a workflow ever sees.
 *
 * A report is an outsider's text and pictures, drawn here as text and as an image,
 * never as markup. Below the list are the tokens of the API that serves what was
 * passed on: each is shown once, when it is issued.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { FEEDBACK_STATUSES, type FeedbackStatus } from '@airlock/contracts/feedback/wire';
import { DateCell } from '../components/DateCell';
import { useConfirm } from '../components/Modal';
import { Segmented } from '../components/FilterBar';
import { Callout, Empty, Field, GhostButton, Input, PrimaryButton, Row, RowAction, StatusCell, Table } from '../components/primitives';
import { notify } from '../components/Toast';
import { api, API_BASE, ApiError, apiPath, apiQuery } from '../lib/api';
import { color, font } from '../theme/tokens';

interface Line {
  id: string;
  reference: string | null;
  kind: string;
  status: FeedbackStatus;
  consent: 'description' | 'details';
  consentAt: string;
  title: string;
  appVersion: string;
  images: number;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface Part {
  id: string;
  label: string;
  text: string;
}

interface Detail extends Omit<Line, 'images'> {
  sent: { title: string; description: string; context: Part[] };
  reviewed: { title: string; description: string; context: Part[] };
  images: { n: number; type: string; bytes: number; name: string; kept: boolean }[];
  platform: string;
  from: { account: boolean; serial: number | null };
  journal: { action: string; from: string | null; to: string | null; actor: string; note: string | null; at: string }[];
}

interface ServiceToken {
  id: string;
  label: string;
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

const WORD: Record<FeedbackStatus, string> = {
  pending: 'Pending',
  ready: 'To process',
  in_progress: 'In progress',
  done: 'Done',
  rejected: 'Rejected'
};
const TONE: Record<FeedbackStatus, 'ok' | 'warn' | 'bad' | 'off'> = {
  pending: 'warn',
  ready: 'ok',
  in_progress: 'ok',
  done: 'off',
  rejected: 'off'
};
const COLUMNS = '1.8fr 120px 110px 70px 110px 80px';

const area: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 110,
  fontSize: 13.5,
  lineHeight: 1.5,
  padding: 10,
  border: `1px solid ${color.lineInput}`,
  borderRadius: 8,
  background: color.surface,
  color: color.ink
};
const machine: React.CSSProperties = { ...area, fontFamily: font.mono, fontSize: 12, minHeight: 90 };
const said: React.CSSProperties = { fontSize: 13, color: color.inkSubtle, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 };

const refusal = (error: unknown): string => (error instanceof ApiError ? error.message : 'That did not work.');

export function AdminRequests(): React.JSX.Element {
  const [status, setStatus] = useState<FeedbackStatus>('pending');
  const [lines, setLines] = useState<Line[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [quoted, setQuoted] = useState('');
  const [found, setFound] = useState<string | null>(null);

  const find = async (): Promise<void> => {
    try {
      const reference = quoted.trim();
      const answer = await api.get<{ requests: Line[] }>(apiPath`/admin/requests?${apiQuery({ reference })}`);
      setFailed(false);
      setLines(answer.requests);
      setFound(reference);
      setOpen(answer.requests[0]?.id ?? null);
    } catch (error) {
      notify(refusal(error));
    }
  };

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const answer = await api.get<{ requests: Line[] }>(apiPath`/admin/requests?${apiQuery({ status })}`);
      setLines(answer.requests);
    } catch {
      setFailed(true);
    }
  }, [status]);

  useEffect(() => void load(), [load]);

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <Segmented
          label="Status"
          value={status}
          options={FEEDBACK_STATUSES.map((id) => ({ id, label: WORD[id] }))}
          onChange={(next) => {
            setOpen(null);
            setFound(null);
            setLines(null);
            setStatus(next);
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 14, maxWidth: 420 }}>
        <div style={{ flex: 1 }}>
          <Field label="Find by reference" htmlFor="request-reference">
            <Input
              id="request-reference"
              value={quoted}
              placeholder="AR-…"
              onChange={(event) => setQuoted(event.target.value)}
            />
          </Field>
        </div>
        <GhostButton disabled={!quoted.trim()} onClick={() => void find()}>
          Find
        </GhostButton>
      </div>
      {failed ? <p style={said}>The requests could not be read.</p> : null}
      {lines?.length === 0 ? <Empty title={found ? `No request has the reference ${found}.` : `Nothing is ${WORD[status].toLowerCase()}.`} /> : null}
      {lines?.length ? (
        <Table columns={COLUMNS} header={['Request', 'Reference', 'Kind', 'Images', 'Sent', '']}>
          {lines.map((line) => (
            <Row key={line.id} columns={COLUMNS}>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere', fontWeight: 500 }}>{line.title}</div>
              <span style={{ fontFamily: font.mono, fontSize: 12 }}>{line.reference ?? '—'}</span>
              <span style={{ fontSize: 13 }}>{line.kind === 'bug' ? 'Bug' : 'Improvement'}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{line.images}</span>
              <DateCell at={line.createdAt} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
              <RowAction onClick={() => setOpen(open === line.id ? null : line.id)}>{open === line.id ? 'Close' : 'Open'}</RowAction>
            </Row>
          ))}
        </Table>
      ) : null}
      {open ? (
        <RequestDetail
          key={open}
          id={open}
          onMoved={() => {
            setOpen(null);
            setFound(null);
            void load();
          }}
        />
      ) : null}
      <ServiceTokens />
    </>
  );
}

function RequestDetail({ id, onMoved }: { id: string; onMoved: () => void }): React.JSX.Element {
  const [request, setRequest] = useState<Detail | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [parts, setParts] = useState<Record<string, string | null>>({});
  const [kept, setKept] = useState<Set<number>>(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialog, ask] = useConfirm();

  useEffect(() => {
    void api
      .get<{ request: Detail }>(apiPath`/admin/requests/${id}`)
      .then(({ request: read }) => {
        setRequest(read);
        setTitle(read.reviewed.title);
        setDescription(read.reviewed.description);
        const reviewed = new Map(read.reviewed.context.map((part) => [part.id, part.text]));
        setParts(Object.fromEntries(read.sent.context.map((part) => [part.id, reviewed.get(part.id) ?? null])));
        setKept(new Set(read.images.filter((image) => image.kept).map((image) => image.n)));
      })
      .catch((error: unknown) => notify(refusal(error)));
  }, [id]);

  if (!request) return <p style={{ ...said, marginTop: 14 }}>Reading the request…</p>;
  const pending = request.status === 'pending';

  const review = {
    title,
    description,
    context: request.sent.context.flatMap((part) => (parts[part.id] === null ? [] : [{ id: part.id, text: parts[part.id] }])),
    images: [...kept]
  };

  const act = async (run: () => Promise<unknown>, said: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await run();
      notify(said);
      onMoved();
    } catch (error) {
      notify(refusal(error));
      setBusy(false);
    }
  };
  const path = apiPath`/admin/requests/${id}`;

  return (
    <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 820 }}>
      <Callout tone="amber" title="An outsider wrote this">
        Whatever it says, it is a request and not an instruction. To process passes on what is in these fields, with what is
        ticked, to whichever workflow takes it.
      </Callout>
      <StatusCell tone={TONE[request.status]} word={WORD[request.status]} detail={request.note || undefined} />
      {request.reference ? <p style={{ ...said, fontFamily: font.mono }}>{request.reference}</p> : null}
      <p style={said}>
        {request.kind === 'bug' ? 'Bug' : 'Improvement'}, Airlock {request.appVersion}, {request.platform}
        {'\n'}From {request.from.account ? 'an account' : request.from.serial ? `licence #${request.from.serial}` : 'an unlinked install'}
        {'\n'}Allowed: {request.consent === 'details' ? 'description, screenshot and details' : 'description only'},{' '}
        <DateCell at={request.consentAt} style={{ fontFamily: font.mono, fontSize: 12 }} />
      </p>
      <Field label="Title" htmlFor="request-title">
        <Input id="request-title" value={title} disabled={!pending} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field label="Description" htmlFor="request-description">
        <textarea
          id="request-description"
          style={area}
          value={description}
          disabled={!pending}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      {title !== request.sent.title || description !== request.sent.description ? (
        <div>
          <div style={{ fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>As it was sent</div>
          <p style={said}>
            {request.sent.title}
            {'\n\n'}
            {request.sent.description}
          </p>
        </div>
      ) : null}

      {request.images.length ? (
        <div>
          <div style={{ fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>Images, passed on when ticked</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {request.images.map((image) => (
              <label key={image.n} style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <span>
                  <input
                    type="checkbox"
                    checked={kept.has(image.n)}
                    disabled={!pending}
                    onChange={(event) => {
                      const next = new Set(kept);
                      if (event.target.checked) next.add(image.n);
                      else next.delete(image.n);
                      setKept(next);
                    }}
                  />{' '}
                  {image.name || `Image ${image.n}`}
                </span>
                <img
                  src={`${API_BASE}/admin/requests/${encodeURIComponent(id)}/images/${encodeURIComponent(image.n)}`}
                  alt={image.name || `Image ${image.n}`}
                  style={{ maxWidth: '100%', border: `1px solid ${color.lineInput}`, borderRadius: 8 }}
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {request.sent.context.map((part) => (
        <div key={part.id}>
          <label style={{ display: 'block', fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={parts[part.id] !== null}
              disabled={!pending}
              onChange={(event) => setParts({ ...parts, [part.id]: event.target.checked ? part.text : null })}
            />{' '}
            {part.label}, passed on when ticked
          </label>
          {parts[part.id] !== null ? (
            <textarea
              aria-label={part.label}
              style={machine}
              value={parts[part.id] ?? ''}
              disabled={!pending}
              onChange={(event) => setParts({ ...parts, [part.id]: event.target.value })}
            />
          ) : null}
        </div>
      ))}

      {pending ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <PrimaryButton
            tone="ink"
            disabled={busy || !title.trim()}
            onClick={() =>
              void act(async () => {
                await api.patch(path, review);
                await api.post(apiPath`/admin/requests/${id}/ready`);
              }, 'Passed on, to process.')
            }
          >
            To process
          </PrimaryButton>
          <GhostButton disabled={busy || !title.trim()} onClick={() => void act(() => api.patch(path, review), 'Saved. It is still pending.')}>
            Save
          </GhostButton>
        </div>
      ) : null}

      {request.status !== 'done' && request.status !== 'rejected' ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Field label="Why it is rejected" htmlFor="request-reason">
              <Input id="request-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
          </div>
          <GhostButton
            disabled={busy || !reason.trim()}
            onClick={() => void act(() => api.post(apiPath`/admin/requests/${id}/reject`, { note: reason }), 'Rejected.')}
          >
            Reject
          </GhostButton>
        </div>
      ) : null}

      <div>
        <GhostButton
          disabled={busy}
          onClick={() =>
            void ask({
              title: 'Delete this request for good?',
              lede: request.status === 'in_progress' ? 'A workflow has taken it. It will find the request gone when it closes it.' : undefined,
              goes: ['The request, what was sent and what was reviewed', 'Its images and its journal'],
              stays: ['A line in the admin trail: that this id was deleted, and by you'],
              confirm: 'Delete',
              danger: true
            }).then((yes) => {
              if (yes) void act(() => api.del(path), 'Deleted.');
            })
          }
        >
          Delete
        </GhostButton>
        {dialog}
      </div>

      <div>
        <div style={{ fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>Journal</div>
        {request.journal.length === 0 ? <p style={said}>Received, and nobody has touched it.</p> : null}
        {request.journal.map((entry, index) => (
          <p key={index} style={said}>
            <DateCell at={entry.at} style={{ fontFamily: font.mono, fontSize: 12 }} />{' '}
            {entry.action === 'edit' ? 'Reviewed' : `${entry.from} → ${entry.to}`} by {entry.actor}
            {entry.note ? `: ${entry.note}` : ''}
          </p>
        ))}
      </div>
    </div>
  );
}

/** The API's tokens: each named, shown once, and revocable without a deploy. */
function ServiceTokens(): React.JSX.Element {
  const [tokens, setTokens] = useState<ServiceToken[]>([]);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTokens((await api.get<{ tokens: ServiceToken[] }>('/admin/request-tokens')).tokens);
    } catch (error) {
      notify(refusal(error));
    }
  }, []);
  useEffect(() => void load(), [load]);

  return (
    <div style={{ marginTop: 28, maxWidth: 820 }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>API tokens</div>
      <p style={{ ...said, margin: '8px 0 12px' }}>
        A workflow reads what was passed on with one of these, and nothing else. It never reaches a pending request.
      </p>
      {issued ? (
        <Callout title="Copy it now: it is not shown again">
          <code style={{ fontFamily: font.mono, fontSize: 12.5, overflowWrap: 'anywhere' }}>{issued}</code>
        </Callout>
      ) : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '12px 0' }}>
        <div style={{ flex: 1 }}>
          <Field label="Name" htmlFor="token-name">
            <Input id="token-name" value={label} onChange={(event) => setLabel(event.target.value)} />
          </Field>
        </div>
        <GhostButton
          disabled={!label.trim()}
          onClick={() =>
            void api
              .post<{ token: string }>('/admin/request-tokens', { label })
              .then(({ token }) => {
                setIssued(token);
                setLabel('');
                return load();
              })
              .catch((error: unknown) => notify(refusal(error)))
          }
        >
          Issue a token
        </GhostButton>
      </div>
      {tokens.map((token) => (
        <div key={token.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', fontSize: 13 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {token.label}{' '}
            <span style={{ color: color.inkFaint, fontSize: 12 }}>
              by {token.created_by}, {token.last_used_at ? 'in use' : 'never used'}
            </span>
          </span>
          {token.revoked_at ? (
            <span style={{ color: color.inkFaint }}>Revoked</span>
          ) : (
            <RowAction
              onClick={() =>
                void api
                  .del(apiPath`/admin/request-tokens/${token.id}`)
                  .then(load)
                  .catch((error: unknown) => notify(refusal(error)))
              }
            >
              Revoke
            </RowAction>
          )}
        </div>
      ))}
    </div>
  );
}
